import axios from 'axios';
import { httpApi } from './http';
import type { ExpenseRequest, ExpenseRequestListParams, ExpenseRequestListResult } from '../types/expenseRequest';

const api = httpApi;

const pickArray = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
};

const pickNumber = (payload: any, keys: string[], fallback: number) => {
  for (const k of keys) {
    const v = payload?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return fallback;
};

export class ExpenseRequestService {
  static async listExpenseRequests(params: ExpenseRequestListParams = {}): Promise<ExpenseRequestListResult> {
    try {
      const response = await api.get<any>('/expense/request', {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 50,
          sort: params.sort ?? '-createdAt',
          ...(params.search ? { search: params.search } : {}),
          ...(params.email ? { email: params.email } : {}),
          ...(params.phone ? { phone: params.phone } : {}),
          ...(params.type ? { type: params.type } : {}),
          ...(params.creditCard ? { creditCard: params.creditCard } : {}),
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as ExpenseRequest[];
      const total = pickNumber(payload, ['total', 'count', 'countTotal'], items.length);
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);

      return { items, total, page, pages };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch expense requests');
    }
  }

  static async getExpenseRequestById(id: string): Promise<ExpenseRequest> {
    try {
      // Postman collection doesn't include a single-item endpoint.
      // Follow the TripRequests pattern: fetch a large list and filter by ID.
      const response = await api.get<any>('/expense/request', {
        params: {
          page: 1,
          limit: 1000,
          sort: '-createdAt',
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as ExpenseRequest[];
      const req = items.find((item) => item._id === id);

      if (!req) throw new Error(`Expense request with ID ${id} not found`);
      return req;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      if (error instanceof Error) throw error;
      throw new Error('Failed to fetch expense request');
    }
  }
}
